import { CompactSign, importPKCS8 } from "jose";
import { UnauthorizedError } from "../domain/error/errors.ts";

const ALG = "EdDSA";

function normalizePem(pem: string): string {
  return pem.replace(/\\n/g, "\n");
}

const privateKeyCache = new Map<string, CryptoKey>();
const publicKeyCache = new Map<string, CryptoKey>();

async function getPrivateKey(pem: string): Promise<CryptoKey> {
  const cached = privateKeyCache.get(pem);
  if (cached) {
    return cached;
  }
  const key = await importPKCS8(normalizePem(pem), ALG);
  privateKeyCache.set(pem, key);
  return key;
}

const base64ToBytes = (base64: string): Uint8Array<ArrayBuffer> =>
  Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

const base64UrlToBytes = (base64Url: string): Uint8Array<ArrayBuffer> =>
  base64ToBytes(base64Url.replaceAll("-", "+").replaceAll("_", "/"));

async function getPublicKey(pem: string): Promise<CryptoKey> {
  const cached = publicKeyCache.get(pem);
  if (cached) {
    return cached;
  }
  const der = base64ToBytes(normalizePem(pem).replaceAll(/-----(BEGIN|END) PUBLIC KEY-----|\s/g, ""));
  const key = await crypto.subtle.importKey("spki", der, { name: "Ed25519" }, false, ["verify"]);
  publicKeyCache.set(pem, key);
  return key;
}

interface SignRolesParams {
  userId: string;
  roles: string[];
  privateKeyPem: string;
}

export async function signRoles({ userId, roles, privateKeyPem }: SignRolesParams): Promise<string> {
  const key = await getPrivateKey(privateKeyPem);
  const payload = new TextEncoder().encode(JSON.stringify({ sub: userId, roles, iat: Math.floor(Date.now() / 1000) }));
  return new CompactSign(payload).setProtectedHeader({ alg: ALG }).sign(key);
}

interface VerifySignedRolesParams {
  signedRoles: string;
  expectedUserId: string;
  publicKeyPem: string;
}

interface VerifiedRoles {
  roles: string[];
  userId: string;
}

/** Native WebCrypto rather than jose: the front verifies at startup, this keeps jose out of its initial bundle. */
export async function verifySignedRoles({
  signedRoles,
  expectedUserId,
  publicKeyPem,
}: VerifySignedRolesParams): Promise<VerifiedRoles> {
  const parts = signedRoles.split(".");
  const [header, payload, signature] = parts;
  if (parts.length !== 3 || header === undefined || payload === undefined || signature === undefined) {
    throw new UnauthorizedError("Malformed signed roles");
  }
  const { alg, crit } = JSON.parse(new TextDecoder().decode(base64UrlToBytes(header))) as {
    alg?: unknown;
    crit?: unknown;
  };
  if (alg !== ALG || crit !== undefined) {
    throw new UnauthorizedError("Unsupported signed roles header");
  }
  const key = await getPublicKey(publicKeyPem);
  const isValid = await crypto.subtle.verify(
    { name: "Ed25519" },
    key,
    base64UrlToBytes(signature),
    new TextEncoder().encode(`${header}.${payload}`),
  );
  if (!isValid) {
    throw new UnauthorizedError("Invalid signed roles signature");
  }
  const data = JSON.parse(new TextDecoder().decode(base64UrlToBytes(payload))) as { sub: string; roles: string[] };

  if (data.sub !== expectedUserId) {
    throw new UnauthorizedError("Signed roles userId mismatch");
  }

  return { roles: data.roles, userId: data.sub };
}

interface VerifySignedRolesWithKeyListParams {
  signedRoles: string;
  expectedUserId: string;
  publicKeyPems: string[];
}

export async function verifySignedRolesWithKeyList({
  signedRoles,
  expectedUserId,
  publicKeyPems,
}: VerifySignedRolesWithKeyListParams): Promise<VerifiedRoles> {
  if (publicKeyPems.length === 0) {
    throw new UnauthorizedError("No public keys provided");
  }

  const attempts = publicKeyPems.map((publicKeyPem) =>
    verifySignedRoles({ signedRoles, expectedUserId, publicKeyPem }).then(
      (result) => ({ ok: true as const, result }),
      (error: unknown) => ({ ok: false as const, error }),
    ),
  );
  const results = await Promise.all(attempts);

  const success = results.find((r) => r.ok);
  if (success) {
    return success.result;
  }

  const lastFailure = results.at(-1);
  if (lastFailure && !lastFailure.ok) {
    throw lastFailure.error;
  }
  throw new UnauthorizedError("No matching key found");
}
